import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { useMentionAutocomplete } from "../../hooks/useMentionAutocomplete";
import MentionSuggestions from "../mentions/MentionSuggestions";

// Text box for new comments, replies, and edits. `target` is the explicitly
// chosen action ({ kind: "reply" | "edit", comment }) or null for the view's
// default (a root comment on a post, or a reply to the focused comment).
//
// `onSubmit(text)` resolves true on success; the text is only cleared then, so
// a failed submission isn't lost. Submitting is blocked while one is in flight.
//
// Typing "@" offers @mention suggestions (useMentionAutocomplete); a typed
// "@username" works the same without them. Post and occurrence comments,
// replies and edits all come through here.
export default function CommentComposer({
  inputRef,
  target,
  placeholder,
  defaultContextLabel,
  disabledMessage,
  onCancelTarget,
  onSubmit,
}) {
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const previousTargetRef = useRef(null);
  const fallbackInputRef = useRef(null);
  const fieldRef = inputRef ?? fallbackInputRef;
  const mention = useMentionAutocomplete({ value: text, onValueChange: setText, inputRef: fieldRef });

  // Starting an edit loads the comment's text; leaving an edit clears it.
  useEffect(() => {
    const previous = previousTargetRef.current;
    if (target?.kind === "edit") {
      setText(target.comment.text);
    } else if (previous?.kind === "edit") {
      setText("");
    }
    previousTargetRef.current = target;
  }, [target]);

  const trimmed = text.trim();
  const disabled = !!disabledMessage;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!trimmed || disabled || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const ok = await onSubmit(trimmed);
      if (ok) setText("");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  let contextLabel = defaultContextLabel;
  if (target?.kind === "reply") contextLabel = `Replying to @${target.comment.user}`;
  if (target?.kind === "edit") contextLabel = "Editing your comment";

  const submitLabel = target?.kind === "edit" ? "Save" : "Post";
  const canSubmit = !!trimmed && !disabled && !submitting;

  return (
    <form onSubmit={handleSubmit} className="mb-4 pt-2 border-t border-gray-300">
      {!disabled && contextLabel && (
        <div className="flex items-center gap-1 mb-1 text-xs text-gray-500">
          <span className="truncate">{contextLabel}</span>
          {target && (
            <button
              type="button"
              onClick={onCancelTarget}
              aria-label={target.kind === "edit" ? "Cancel edit" : "Cancel reply"}
              className="rounded p-0.5 hover:bg-gray-100 hover:text-gray-800"
            >
              <X size={12} aria-hidden="true" />
            </button>
          )}
        </div>
      )}
      <div className="flex items-center gap-2">
        <input
          type="text"
          ref={fieldRef}
          value={text}
          {...mention.inputProps}
          onKeyDown={(e) => {
            if (mention.handleKeyDown(e)) return;
            if (e.key === "Escape" && target) onCancelTarget();
          }}
          role="combobox"
          aria-autocomplete={mention.ariaProps["aria-autocomplete"]}
          aria-expanded={mention.ariaProps["aria-expanded"]}
          aria-controls={mention.ariaProps["aria-controls"]}
          aria-activedescendant={mention.ariaProps["aria-activedescendant"]}
          placeholder={disabled ? disabledMessage : placeholder}
          aria-label={contextLabel || placeholder}
          disabled={disabled}
          readOnly={submitting}
          className="border p-2 w-full rounded disabled:bg-gray-50 disabled:cursor-not-allowed"
        />
        <button
          type="submit"
          disabled={!canSubmit}
          className={`${
            canSubmit ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-400 cursor-not-allowed"
          } px-3 py-1 rounded-full`}
        >
          {submitting ? "…" : submitLabel}
        </button>
      </div>
      {!disabled && mention.suggestions && <MentionSuggestions {...mention.suggestions} />}
    </form>
  );
}
